import React, { useState } from 'react';
import {
  Typography,
  Paper,
  List,
  ListItem,
  Box,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Fade,
  Chip,
  Grid,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import ts from 'react-syntax-highlighter/dist/esm/languages/hljs/typescript';
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import css from 'react-syntax-highlighter/dist/esm/languages/hljs/css';
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import type { DocMeta } from '@components'

SyntaxHighlighter.registerLanguage('typescript', ts);
SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('bash', bash);

type CustomCodeProps = {
  inline?: boolean;
  children?: React.ReactNode;
  className?: string;
};

const AlertBlock = ({ type, children }: { type: string; children: React.ReactNode }) => {
  const configs = {
    info: { color: '#42a5f5', icon: <InfoIcon />, title: 'Info' },
    warning: { color: '#ffa726', icon: <WarningIcon />, title: 'Warning' },
    error: { color: '#ef5350', icon: <ErrorIcon />, title: 'Error' },
    success: { color: '#66bb6a', icon: <CheckCircleIcon />, title: 'Success' },
  };

  const config = configs[type as keyof typeof configs] || configs.info;

  return (
    <Alert
      icon={config.icon}
      severity={type as any}
      sx={{
        my: 3,
        borderRadius: 2,
        border: `1px solid ${config.color}40`,
        backgroundColor: `${config.color}10`,
        '& .MuiAlert-icon': { color: config.color },
      }}
    >
      <AlertTitle sx={{ color: config.color, fontWeight: 600 }}>{config.title}</AlertTitle>
      <Box sx={{ color: 'rgba(255,255,255,0.9)' }}>{children}</Box>
    </Alert>
  );
};

export function LayoutRenderer({
  meta,
  content,
}: {
  meta: DocMeta;
  content: string;
}): React.ReactElement {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const components: Components = {
    h1: ({ children }) => (
      <Typography
        variant="h4"
        gutterBottom
        fontWeight={700}
        sx={{
          mt: 4,
          mb: 2,
          color: '#fff',
          borderBottom: '2px solid rgba(102, 126, 234, 0.3)',
          pb: 1,
          display: 'inline-block',
        }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        variant="h5"
        gutterBottom
        fontWeight={600}
        sx={{
          mt: 4,
          mb: 2,
          color: '#667eea',
        }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        variant="h6"
        gutterBottom
        fontWeight={600}
        sx={{
          mt: 3,
          mb: 1,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography
        variant="body1"
        paragraph
        sx={{
          lineHeight: 1.8,
          color: 'rgba(255,255,255,0.8)',
          mb: 2,
        }}
      >
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ color: '#fff', fontWeight: 700 }}>
        {children}
      </Box>
    ),
    em: ({ children }) => (
      <Box component="span" sx={{ color: '#a0a0a0', fontStyle: 'italic' }}>
        {children}
      </Box>
    ),
    code: ({ inline, children, className }: CustomCodeProps) => {
      const codeText = String(children).replace(/\n$/, '');
      const key = `${className}-${codeText.slice(0, 8)}`;

      if (inline) {
        return (
          <Chip
            size="small"
            label={children}
            sx={{
              fontFamily: 'monospace',
              backgroundColor: 'rgba(102, 126, 234, 0.2)',
              color: '#667eea',
              fontWeight: 600,
              height: '24px',
            }}
          />
        );
      }

      const match = /language-(\w+)/.exec(className || '');
      const language = match?.[1] || 'text';

      return (
        <Box
          sx={{
            position: 'relative',
            my: 3,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              backgroundColor: '#161b22',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 600,
                letterSpacing: '1px',
              }}
            >
              {language}
            </Typography>
            <Tooltip
              title={copied === key ? 'Copied!' : 'Copy to clipboard'}
              arrow
              placement="left"
            >
              <IconButton
                onClick={() => handleCopy(codeText, key)}
                size="small"
                sx={{
                  color: copied === key ? '#4caf50' : 'rgba(255,255,255,0.7)',
                  '&:hover': { color: '#fff' },
                }}
              >
                {copied === key ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <SyntaxHighlighter
            language={language}
            style={atomOneDark}
            showLineNumbers
            customStyle={{
              margin: 0,
              padding: '1.5rem',
              backgroundColor: '#0d1117',
              fontSize: '0.875rem',
              lineHeight: 1.6,
            }}
            lineNumberStyle={{
              color: '#555',
              fontSize: '0.75rem',
              marginRight: '16px',
              minWidth: '2em',
            }}
          >
            {codeText}
          </SyntaxHighlighter>
        </Box>
      );
    },
    a: ({ href, children }) => (
      <MuiLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
        sx={{
          color: '#667eea',
          fontWeight: 500,
          transition: 'all 0.2s',
          '&:hover': {
            color: '#764ba2',
            textDecoration: 'underline',
          },
        }}
      >
        {children}
      </MuiLink>
    ),
    img: ({ src = '', alt = '' }) => (
      <Box
        sx={{
          my: 3,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            width: '100%',
            display: 'block',
            transition: 'transform 0.3s',
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
        />
        {alt && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              py: 1,
              color: 'rgba(255,255,255,0.5)',
              backgroundColor: 'rgba(0,0,0,0.3)',
            }}
          >
            {alt}
          </Typography>
        )}
      </Box>
    ),
    ul: ({ children }) => (
      <List
        sx={{
          listStyle: 'none',
          pl: 0,
          m: 0,
          mb: 3,
          '& li': {
            position: 'relative',
            pl: 3,
            py: 0.5,
            '&::before': {
              content: '"•"',
              position: 'absolute',
              left: 0,
              color: '#667eea',
              fontWeight: 'bold',
              fontSize: '1.2rem',
            },
          },
        }}
      >
        {children}
      </List>
    ),
    ol: ({ children }) => (
      <List
        component="ol"
        sx={{
          listStyle: 'none',
          counterReset: 'item',
          pl: 0,
          m: 0,
          mb: 3,
          '& li': {
            position: 'relative',
            pl: 3,
            py: 0.5,
            counterIncrement: 'item',
            '&::before': {
              content: 'counter(item) "."',
              position: 'absolute',
              left: 0,
              color: '#667eea',
              fontWeight: 'bold',
            },
          },
        }}
      >
        {children}
      </List>
    ),
    li: ({ children }) => (
      <ListItem sx={{ display: 'list-item', color: 'rgba(255,255,255,0.8)' }}>
        {children}
      </ListItem>
    ),
    blockquote: ({ children }) => (
      <Box
        sx={{
          my: 3,
          pl: 3,
          borderLeft: '4px solid #667eea',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          py: 2,
          pr: 2,
          borderRadius: '0 8px 8px 0',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            fontStyle: 'italic',
            lineHeight: 1.6,
          }}
        >
          {children}
        </Typography>
      </Box>
    ),
    hr: () => (
      <Divider
        sx={{
          my: 4,
          borderColor: 'rgba(255,255,255,0.1)',
        }}
      />
    ),
    table: ({ children }) => (
      <Box
        sx={{
          my: 3,
          overflowX: 'auto',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }) => (
      <Box component="thead" sx={{ backgroundColor: 'rgba(102, 126, 234, 0.2)' }}>
        {children}
      </Box>
    ),
    tbody: ({ children }) => (
      <Box component="tbody">{children}</Box>
    ),
    tr: ({ children }) => (
      <Box
        component="tr"
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        {children}
      </Box>
    ),
    th: ({ children }) => (
      <Box
        component="th"
        sx={{
          p: 2,
          textAlign: 'left',
          color: '#fff',
          fontWeight: 600,
          borderBottom: '2px solid rgba(102, 126, 234, 0.3)',
        }}
      >
        {children}
      </Box>
    ),
    td: ({ children }) => (
      <Box
        component="td"
        sx={{
          p: 2,
          color: 'rgba(255,255,255,0.8)',
        }}
      >
        {children}
      </Box>
    ),
  };

  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
      }}
    >
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {content}
      </ReactMarkdown>
    </Box>
  );
}